import { useQuery } from "@tanstack/react-query";

import { BusinessesResponse, WebstoreOverviewResponse } from "../types/webstoreAnalytics";

/**
 * The API returns a useful `detail` on every failure — "not configured", "range too
 * wide", "database unreachable". Surfacing it beats a bare status code, because each
 * one implies a different fix.
 */
async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url);

  if (!res.ok) {
    let message = `Request failed with status ${res.status}`;
    try {
      const body = await res.json();
      if (body?.detail) {
        message = typeof body.detail === "string" ? body.detail : JSON.stringify(body.detail);
      }
    } catch {
      // Non-JSON error body — the status message is all we have.
    }
    throw new Error(message);
  }

  return res.json();
}

export function getWebstoreOverview(
  startDate: string,
  endDate: string,
  businessId: string
): Promise<WebstoreOverviewResponse> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    business_id: businessId,
  });
  return getJson<WebstoreOverviewResponse>(`/api/webstore-analytics/overview?${params}`);
}

export function useWebstoreOverview(startDate: string, endDate: string, businessId: string) {
  return useQuery<WebstoreOverviewResponse, Error>({
    queryKey: ["webstoreOverview", startDate, endDate, businessId],
    queryFn: () => getWebstoreOverview(startDate, endDate, businessId),
    retry: false,
  });
}

export function getWebstoreBusinesses(
  startDate: string,
  endDate: string
): Promise<BusinessesResponse> {
  const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
  return getJson<BusinessesResponse>(`/api/webstore-analytics/businesses?${params}`);
}

/**
 * The dropdown deliberately does not depend on the selected business — /overview is
 * filtered to it, so it cannot also be the list you pick from.
 */
export function useWebstoreBusinesses(startDate: string, endDate: string) {
  return useQuery<BusinessesResponse, Error>({
    queryKey: ["webstoreBusinesses", startDate, endDate],
    queryFn: () => getWebstoreBusinesses(startDate, endDate),
    retry: false,
  });
}
